<template>
  <div
    ref="rootRef"
    class="v3-custom-select ps__child--consume"
    :class="{
      'is-invalid': invalidFeedback || isInvalid
    }"
    :title="nativeTitleAttr"
  >
    <label
      v-if="resolvedVisibleLabel"
      :id="comboboxFloatingLabelId"
      :for="resolvedInputId"
      class="v3-form-label d-block mb-1"
      >{{ resolvedVisibleLabel }}</label
    >
    <VSelect
      ref="vselectRef"
      :model-value="modelValue"
      v-bind="vueSelectBindAttrs"
      :map-keydown="vueSelectMapKeydown"
      @search="onSearch"
      @search:focus="onVSelectSearchFocus"
      @update:model-value="onChange"
      @open="onOpenSelect"
      @close="onCloseSelect"
    >
      <template v-if="customTemplate" #option="option">
        <slot name="custom-template" :option="option"></slot>
      </template>
      <template v-if="selectOnlyNames" #selected-option="{ label }">
        <span class="selected-option-label" :aria-label="label">{{
          splitText(label)
        }}</span>
      </template>
      <slot name="list-footer"></slot>
      <slot name="footer"></slot>
      <template #no-options>
        <slot name="no-options">
          <!-- Visible copy only; SR announcement uses `#noMatchingOptionsMessageId` (live region outside listbox). -->
          <span class="text-black-50 pm-1 d-block">
            {{ $t("common.sorry_no_matching_options") }}
          </span>
        </slot>
      </template>
      <template #open-indicator="{ attributes }">
        <span class="options-length-label" v-if="selectedOptions > 3"
          >+{{ selectedOptions - 3 }} {{ $t("common.more") }}</span
        >
        <i :class="appendIcon" v-bind="attributes"></i>
      </template>
      <template #spinner="{ loading }">
        <div
          v-show="loading"
          class="vs__spinner"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {{ $t("common.loading_more") }}
        </div>
      </template>
    </VSelect>
    <span
      v-if="selectedValueDescription"
      :id="selectedValueDescriptionId"
      class="visually-hidden"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ selectedValueDescription }}
    </span>
    <span
      v-if="liveStatusMessage"
      :id="liveStatusMessageId"
      :key="`live-status-${liveStatusRenderKey}`"
      class="visually-hidden"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ liveStatusMessage }}
    </span>
    <span
      :id="noMatchingOptionsMessageId"
      :key="`no-match-${noMatchingOptionsLiveKey}`"
      class="visually-hidden"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ noMatchingOptionsMessage }}
    </span>
    <slot name="content"></slot>
    <div
      v-if="invalidFeedbackMessage"
      :id="invalidFeedbackId"
      class="invalid-message text-start"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <span class="visually-hidden">
        {{ resolvedComboboxAriaLabel }}:
        {{ isFieldRequired ? ` ${$t("common.this_field_is_required")}` : "" }}
      </span>
      {{ invalidFeedbackMessage }}
    </div>
  </div>
</template>

<script>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  onUpdated,
  ref,
  watch
} from "vue"
import { useI18n } from "vue-i18n"
import { syncVueSelectListboxAriaHidden } from "@/helpers/vueSelectA11y"
import { announce } from "@/composables/useLiveAnnouncer"

const generateSelectInputId = () =>
  `v3-cselect-${Math.random().toString(36).slice(2, 11)}`

const clampValue = (value, maxLength) => {
  const isString = typeof value === "string"
  if (isString) {
    return value?.length > maxLength ? value.slice(0, maxLength) : value
  }
  return value
}

/**
 * vue-select `appendToBody` + default `calculatePosition` uses `position: absolute` with
 * document coordinates; under PerfectScrollbar that often misplaces or hides the menu.
 * Fixed viewport positioning + scroll parents keeps the list visible and aligned.
 * Clamps height and flips above the toggle when there is not enough space below (viewport edge).
 */
const appendBodyFixedCalculatePosition = (dropdown, component, initial) => {
  const toggle = component?.$refs?.toggle
  const applyInitialPlacement = () => {
    if (initial && typeof initial === "object") {
      if (initial.width != null) dropdown.style.width = initial.width
      if (initial.left != null) dropdown.style.left = initial.left
      if (initial.top != null) dropdown.style.top = initial.top
    }
    dropdown.classList.add("v3-vs-dropdown-menu--append-body")
  }

  if (!toggle?.getBoundingClientRect) {
    applyInitialPlacement()
    return () => {
      dropdown.classList?.remove?.("v3-vs-dropdown-menu--append-body")
    }
  }

  const margin = 10
  /** Aligns with theme `form.scss` / `selects.scss` list cap */
  const maxMenuCap = 220

  const apply = () => {
    const rect = toggle.getBoundingClientRect()
    const vv = window.visualViewport
    const viewportH =
      vv?.height || document.documentElement?.clientHeight || window.innerHeight
    const viewportW =
      vv?.width || document.documentElement?.clientWidth || window.innerWidth

    const spaceBelow = viewportH - rect.bottom - margin
    const spaceAbove = rect.top - margin
    const preferAbove =
      spaceBelow < Math.min(120, maxMenuCap) && spaceAbove > spaceBelow

    let maxHeightPx = Math.min(
      maxMenuCap,
      Math.max(72, preferAbove ? spaceAbove : spaceBelow)
    )

    let leftPx = rect.left
    const menuWidth = rect.width
    if (leftPx + menuWidth > viewportW - margin) {
      leftPx = Math.max(margin, viewportW - menuWidth - margin)
    }

    dropdown.classList.add("v3-vs-dropdown-menu--append-body")

    const set = (name, value) => {
      dropdown.style.setProperty(name, value, "important")
    }

    set("position", "fixed")
    set("width", `${menuWidth}px`)
    set("left", `${leftPx}px`)
    set("max-height", `${Math.floor(maxHeightPx)}px`)

    if (preferAbove) {
      set("top", "auto")
      set("bottom", `${viewportH - rect.top + 4}px`)
    } else {
      set("bottom", "auto")
      set("top", `${rect.bottom}px`)
    }
  }

  const scrollableAncestors = []
  let el = toggle.parentElement
  while (el && el !== document.body) {
    try {
      const style = window.getComputedStyle(el)
      const y = style.overflowY
      const x = style.overflowX
      if (/(auto|scroll|overlay)/.test(y) || /(auto|scroll|overlay)/.test(x)) {
        scrollableAncestors.push(el)
        el.addEventListener("scroll", apply, { passive: true })
      }
    } catch {
      /* ignore */
    }
    el = el.parentElement
  }

  window.addEventListener("resize", apply)
  window.addEventListener("scroll", apply, true)
  apply()

  return () => {
    window.removeEventListener("resize", apply)
    window.removeEventListener("scroll", apply, true)
    scrollableAncestors.forEach((node) => {
      node.removeEventListener("scroll", apply)
    })
    dropdown.classList?.remove?.("v3-vs-dropdown-menu--append-body")
  }
}

export default {
  // For adding more props or functionalities for v-select check its own: https://vue-select.org/api/props.html
  props: {
    selected: Object,
    multiple: Boolean,
    options: Array,
    clearable: Boolean,
    placeholder: String,
    /** When true (default), vue-select keeps the highlighted option in view while navigating (Arrow Up/Down). Keep false only if it conflicts with a custom dropdown. */
    autoscroll: {
      type: Boolean,
      default: true
    },
    closeOnSelect: Boolean,
    filterable: Boolean,
    reduce: Function,
    selectable: Function,
    searchable: { type: Boolean, default: true },
    required: { type: Boolean, default: false },
    title: String,
    disabled: Boolean,
    deselectFromDropdown: Boolean,
    modelValue: [String, Object, Array],
    invalidFeedback: [String, Array],
    isInvalid: Boolean,
    readonly: { type: Boolean, default: false },
    appendIcon: {
      type: String,
      default: "ri-arrow-down-s-line"
    },
    clearCacheOnBlur: Function,
    /** Associates a visible label with the vue-select search input (`for` / `input-id`). */
    accessibleLabel: {
      type: String,
      default: ""
    },
    /** When true, hide the inner floating label (use an external `<Label>` only). */
    hideFloatingLabel: {
      type: Boolean,
      default: false
    },
    /**
     * When `hide-floating-label` is true: `id` of the external `<label>` (e.g. `Label`’s `label-id`) for
     * `aria-labelledby`. Prefer this over duplicate `aria-label` when the visible label lives outside `CustomSelect`.
     * Space-separated ids are allowed (multiple labelling elements).
     */
    labelledBy: {
      type: String,
      default: ""
    },
    /** Explicit id for the search input (preferred for stable labels in tests). */
    inputId: {
      type: String,
      default: ""
    },
    /** @deprecated Use inputId; kept for callers that still pass id. */
    id: {
      type: String,
      default: ""
    },
    selectOnlyNames: {
      type: Boolean,
      default: false
    },
    customTemplate: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: 500
    },
    /** Passed to vue-select search input (e.g. `off` to avoid inaccurate autocomplete purpose). */
    autocomplete: {
      type: String,
      default: "off"
    },
    /**
     * When false (default), the options list does not open on Tab focus alone so screen readers
     * can announce the field label before the list. Open with pointer (click / label click), Arrow keys,
     * Enter, Space (non-filterable), or by typing when filterable.
     * Set true to restore vue-select's default (open on every focus).
     */
    openOnFocus: {
      type: Boolean,
      default: false
    },
    /**
     * Portals the listbox to `document.body` with fixed positioning so it is not clipped by
     * `overflow` on main / PerfectScrollbar (e.g. Modules › Kiosk default domain).
     */
    appendDropdownToBody: {
      type: Boolean,
      default: false
    },
    /**
     * When true with `appendDropdownToBody`, uses custom fixed-viewport positioning for scroll
     * containers (e.g. PerfectScrollbar). Leave false in modals — vue-select's default
     * `calculatePosition` works there; the custom fixed path can hide menus in some layouts.
     */
    appendDropdownFixedPosition: {
      type: Boolean,
      default: false
    }
  },
  emits: ["search", "update:modelValue", "open", "close"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const vselectRef = ref(null)
    const rootRef = ref(null)
    const fallbackInputId = generateSelectInputId()
    const resolvedInputId = computed(
      () => props.inputId || props.id || fallbackInputId
    )
    const invalidFeedbackId = computed(
      () => `${resolvedInputId.value}-feedback`
    )
    const selectedValueDescriptionId = computed(
      () => `${resolvedInputId.value}-selection`
    )
    const liveStatusMessageId = computed(
      () => `${resolvedInputId.value}-status`
    )
    const noMatchingOptionsMessageId = computed(
      () => `${resolvedInputId.value}-no-match`
    )
    const noMatchingOptionsMessage = ref("")
    const noMatchingOptionsLiveKey = ref(0)
    const liveStatusRenderKey = ref(0)
    let noMatchingOptionsSyncTimer = null
    let openEmptyCheckTimers = []
    let focusEmptyAnnounceTimer = null

    /** True when the last pointerdown before focus was on this field, its label[for], or inside vue-select. */
    const pendingPointerFieldActivation = ref(false)

    const resolvedVisibleLabel = computed(() => {
      if (props.hideFloatingLabel) return ""

      const label = props.accessibleLabel?.trim?.()
      if (label) return label

      const placeholder = props.placeholder?.trim?.()
      const genericSelectLabel = t("common.select")
      const selectOneLabel = t("common.select_one")
      if (
        placeholder &&
        placeholder !== genericSelectLabel &&
        placeholder !== selectOneLabel
      ) {
        return placeholder
      }
      return ""
    })

    /** Stable id for the visible `<label>` so the combobox can use `aria-labelledby` (APG). */
    const comboboxFloatingLabelId = computed(
      () => `${resolvedInputId.value}-floating-label`
    )

    const externalLabelledBy = computed(() => props.labelledBy?.trim?.() || "")

    const isFieldRequired = computed(() => !!props.required)

    /** vue-select hardcodes combobox aria-label "Search for option" — override per field. */
    const resolvedComboboxAriaLabel = computed(() => {
      const fromAccessible = props.accessibleLabel?.trim?.()
      if (fromAccessible) return fromAccessible
      const label = resolvedVisibleLabel.value
      if (label) return label
      const ph = props.placeholder?.trim?.()
      if (ph) return ph
      return t("common.select")
    })

    /** Shallow signal for option list size — avoids deep watch on large `options` arrays. */
    const optionsLengthSignal = computed(() =>
      Array.isArray(props.options) ? props.options.length : 0
    )

    /**
     * Shallow signal for selection/clear control — when clear button appears or count changes.
     * Avoids deep traversal of `modelValue` (e.g. large multi-select arrays).
     */
    const modelValueSyncSignal = computed(() => {
      const v = props.modelValue
      if (v == null || v === "") return 0
      if (Array.isArray(v)) return v.length
      return 1
    })

    /**
     * vue-select puts #no-options inside `<li>` under `ul[role=listbox]`, so `aria-live` there is often not announced.
     * Mirror the empty-filter state in a sibling live region (APG-friendly).
     */
    const checkAndAnnounceEmptyState = () => {
      const vs = vselectRef.value
      /* vue-select v4 exposes `open`, not `dropdownOpen` — guard must match or empty state is never announced. */
      if (!vs?.open) {
        noMatchingOptionsMessage.value = ""
        return
      }
      if (vs.mutableLoading) {
        return
      }
      const filtered = vs.filteredOptions
      const filteredLen = Array.isArray(filtered) ? filtered.length : 0
      if (filteredLen > 0) {
        noMatchingOptionsMessage.value = ""
        return
      }
      const fieldName = resolvedComboboxAriaLabel.value?.trim?.()
      const msg = t("common.sorry_no_matching_options")
      noMatchingOptionsMessage.value = fieldName ? `${fieldName}. ${msg}` : msg
      noMatchingOptionsLiveKey.value += 1
      /* Keyed sibling region is recreated on change, so announce reliably via the
         persistent global live region as well. Assertive so it isn't dropped while the
         screen reader is busy announcing the just-opened listbox. `force` so repeated
         identical "no matching options" still re-announces on NVDA (Deque 2189113 /
         2200576 / 2189100 / 2174065 / 2183619 / 2174531). */
      announce(noMatchingOptionsMessage.value, { assertive: true, force: true })
    }

    const syncNoMatchingOptionsLiveRegion = () => {
      nextTick(checkAndAnnounceEmptyState)
    }

    const clearOpenEmptyChecks = () => {
      openEmptyCheckTimers.forEach((id) => clearTimeout(id))
      openEmptyCheckTimers = []
    }

    /* When the list opens, the empty state may not be settled yet (options can load
       asynchronously) and the screen reader is mid-announcing the listbox. Re-check a
       few times while it stays open so an empty list announces "no matching options"
       even without typing. The global announcer de-dupes, so this speaks at most once. */
    const scheduleEmptyStateChecksOnOpen = () => {
      clearOpenEmptyChecks()
      ;[60, 250, 600, 1000].forEach((delay) => {
        openEmptyCheckTimers.push(
          setTimeout(() => {
            nextTick(checkAndAnnounceEmptyState)
          }, delay)
        )
      })
    }

    /* With openOnFocus=false the list stays closed on focus, so the open-time checks
       never run. Announce the empty state on focus too (after a beat so it lands after
       "combobox"), skipping while options are still loading to avoid a false negative. */
    const scheduleEmptyStateAnnounceOnFocus = () => {
      if (focusEmptyAnnounceTimer != null) {
        clearTimeout(focusEmptyAnnounceTimer)
      }
      focusEmptyAnnounceTimer = setTimeout(() => {
        focusEmptyAnnounceTimer = null
        const vs = vselectRef.value
        if (!vs || vs.mutableLoading) return
        const filtered = vs.filteredOptions
        const filteredLen = Array.isArray(filtered) ? filtered.length : 0
        if (filteredLen > 0) return
        const fieldName = resolvedComboboxAriaLabel.value?.trim?.()
        const msg = t("common.sorry_no_matching_options")
        announce(fieldName ? `${fieldName}. ${msg}` : msg, {
          assertive: true,
          force: true
        })
      }, 500)
    }

    /** vue-select updates `filteredOptions` after `@search`; defer so empty-filter state is accurate (e.g. CSV Select Type). */
    const scheduleSyncNoMatchingOptionsLiveRegion = () => {
      if (noMatchingOptionsSyncTimer != null) {
        clearTimeout(noMatchingOptionsSyncTimer)
      }
      noMatchingOptionsSyncTimer = setTimeout(() => {
        noMatchingOptionsSyncTimer = null
        syncNoMatchingOptionsLiveRegion()
      }, 75)
    }

    /**
     * vue-select re-applies `scope.search.attributes` on every internal render (e.g. when
     * `filteredOptions` / loading changes). That resets `aria-labelledby` on `.vs__search`
     * to `vs${uid}__combobox` (“Search for option”). Run our fixes after Vue’s DOM patch
     * and the next animation frame so we win over the library’s last write.
     */
    const syncVueSelectAria = () => {
      nextTick(() => {
        requestAnimationFrame(() => {
          applyVueSelectAriaSync()
          /* vue-select reapplies attrs after patch; win on the next frame too (axe/deque combobox name). */
          requestAnimationFrame(() => applyVueSelectAriaSync())
        })
      })
    }

    const applyVueSelectAriaSync = () => {
      const root = vselectRef.value?.$el
      if (!root?.querySelector) return
      const searchInput = root.querySelector(".vs__search")
      const toggleEl =
        root.querySelector('[id$="__combobox"]') ||
        root.querySelector(".vs__dropdown-toggle")
      const describedByIds = []
      if (selectedValueDescription.value) {
        describedByIds.push(selectedValueDescriptionId.value)
      }
      if (invalidFeedbackMessage.value) {
        describedByIds.push(invalidFeedbackId.value)
      }

      /*
       * Combobox w/ listbox popup: W3C ARIA Combobox (APG) — https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
       * vue-select puts role="combobox" on the outer .vs__dropdown-toggle while focus
       * stays on .vs__search — SRs often skip announcing combobox (axe: missing role).
       * Demote the wrapper to presentation and expose a single combobox on the input
       * (role, name, value/selection, expanded, haspopup, controls, activedescendant, autocomplete, etc.).
       */
      const listboxId =
        toggleEl?.getAttribute?.("aria-owns") ||
        searchInput?.getAttribute?.("aria-controls") ||
        ""
      const isOpen = !!root.querySelector(".v-select.vs--open")
      const expandedState = isOpen ? "true" : "false"
      const activeDescendant =
        searchInput?.getAttribute?.("aria-activedescendant") || ""

      const getOrCreateExternalLabelId = () => {
        if (typeof document === "undefined") return ""
        const inputId = resolvedInputId.value
        if (!inputId) return ""
        let externalLabel = null
        try {
          const escaped =
            typeof CSS !== "undefined" && CSS.escape
              ? CSS.escape(inputId)
              : inputId
          externalLabel = document.querySelector(`label[for="${escaped}"]`)
        } catch {
          externalLabel = document.querySelector(`label[for="${inputId}"]`)
        }
        if (!externalLabel) return ""
        if (!externalLabel.id) {
          externalLabel.id = `${inputId}-external-label`
        }
        return externalLabel.id
      }

      /**
       * Non-searchable vue-select (e.g. per-page dropdown) has no `.vs__search`; keep the
       * toggle/combobox root as the single `role="combobox"` so `aria-expanded` is correct (2171088).
       */
      const comboboxWithoutSearchInput =
        !searchInput && (root.querySelector('[id$="__combobox"]') || toggleEl)

      if (toggleEl && searchInput) {
        toggleEl.setAttribute("role", "presentation")
        toggleEl.removeAttribute("aria-expanded")
        toggleEl.removeAttribute("aria-owns")
        toggleEl.removeAttribute("aria-label")
        toggleEl.removeAttribute("aria-describedby")
        toggleEl.removeAttribute("aria-errormessage")
      }

      if (searchInput) {
        const hasFloatingLabel = Boolean(resolvedVisibleLabel.value?.trim?.())
        const externalLabelId = getOrCreateExternalLabelId()
        const hasExternalLabel = Boolean(externalLabelId)
        const useExternalLabelledBy =
          props.hideFloatingLabel && Boolean(externalLabelledBy.value)
        const isTypingFilterable =
          props.searchable !== false && props.filterable !== false
        searchInput.setAttribute("id", resolvedInputId.value)
        /* Expose a single combobox on `.vs__search` because `.vs__dropdown-toggle` is demoted to presentation. */
        /* vue-select renders `type="search"`, but `role="combobox"` is only a valid
         * override on `<input type="text">` per ARIA-in-HTML; some screen readers /
         * validators ignore the combobox role on a search input. Normalize to text. */
        if (searchInput.getAttribute("type") !== "text") {
          searchInput.setAttribute("type", "text")
        }
        searchInput.setAttribute("role", "combobox")
        searchInput.setAttribute("aria-expanded", expandedState)
        searchInput.setAttribute("aria-haspopup", "listbox")
        if (hasExternalLabel) {
          searchInput.setAttribute("aria-labelledby", externalLabelId)
          searchInput.removeAttribute("aria-label")
        } else if (hasFloatingLabel) {
          searchInput.setAttribute(
            "aria-labelledby",
            comboboxFloatingLabelId.value
          )
          searchInput.removeAttribute("aria-label")
        } else if (useExternalLabelledBy) {
          searchInput.setAttribute("aria-labelledby", externalLabelledBy.value)
          searchInput.removeAttribute("aria-label")
        } else {
          searchInput.removeAttribute("aria-labelledby")
          searchInput.setAttribute(
            "aria-label",
            resolvedComboboxAriaLabel.value
          )
        }
        searchInput.setAttribute(
          "aria-autocomplete",
          isTypingFilterable ? "list" : "none"
        )
        /*
         * Deque (2173745/2173751): remove the read-only state from select-only comboboxes.
         * vue-select adds a native `readonly` attribute when `searchable` is false. We strip
         * both the native `readonly` and `aria-readonly` and instead keep the input
         * non-editable with a `beforeinput` guard, so behaviour is unchanged but no
         * read-only state is exposed to assistive tech.
         */
        searchInput.removeAttribute("aria-readonly")
        if (props.searchable === false) {
          searchInput.removeAttribute("readonly")
          if (!searchInput.dataset.selectOnlyGuard) {
            searchInput.dataset.selectOnlyGuard = "true"
            searchInput.addEventListener("beforeinput", preventSelectOnlyTyping)
          }
        }
        searchInput.setAttribute(
          "aria-invalid",
          invalidFeedbackMessage.value ? "true" : "false"
        )
        searchInput.setAttribute(
          "aria-required",
          isFieldRequired.value ? "true" : "false"
        )
        if (listboxId && isOpen) {
          searchInput.setAttribute("aria-controls", listboxId)
        } else {
          searchInput.removeAttribute("aria-controls")
        }
        if (activeDescendant) {
          searchInput.setAttribute("aria-activedescendant", activeDescendant)
        } else {
          searchInput.removeAttribute("aria-activedescendant")
        }
        if (describedByIds.length) {
          searchInput.setAttribute("aria-describedby", describedByIds.join(" "))
        } else {
          searchInput.removeAttribute("aria-describedby")
        }
        if (invalidFeedbackMessage.value) {
          searchInput.setAttribute("aria-errormessage", invalidFeedbackId.value)
        } else {
          searchInput.removeAttribute("aria-errormessage")
        }
      } else if (comboboxWithoutSearchInput) {
        const comboHost = comboboxWithoutSearchInput
        const hasFloatingLabel = Boolean(resolvedVisibleLabel.value?.trim?.())
        const externalLabelId = getOrCreateExternalLabelId()
        const hasExternalLabel = Boolean(externalLabelId)
        const useExternalLabelledBy =
          props.hideFloatingLabel && Boolean(externalLabelledBy.value)
        if (resolvedInputId.value && !comboHost.getAttribute("id")) {
          comboHost.setAttribute("id", resolvedInputId.value)
        }
        comboHost.setAttribute("role", "combobox")
        comboHost.setAttribute("aria-expanded", expandedState)
        comboHost.setAttribute("aria-haspopup", "listbox")
        if (hasExternalLabel) {
          comboHost.setAttribute("aria-labelledby", externalLabelId)
          comboHost.removeAttribute("aria-label")
        } else if (hasFloatingLabel) {
          comboHost.setAttribute(
            "aria-labelledby",
            comboboxFloatingLabelId.value
          )
          comboHost.removeAttribute("aria-label")
        } else if (useExternalLabelledBy) {
          comboHost.setAttribute("aria-labelledby", externalLabelledBy.value)
          comboHost.removeAttribute("aria-label")
        } else {
          comboHost.removeAttribute("aria-labelledby")
          comboHost.setAttribute("aria-label", resolvedComboboxAriaLabel.value)
        }
        comboHost.setAttribute("aria-autocomplete", "none")
        comboHost.removeAttribute("aria-readonly")
        comboHost.setAttribute(
          "aria-invalid",
          invalidFeedbackMessage.value ? "true" : "false"
        )
        comboHost.setAttribute(
          "aria-required",
          isFieldRequired.value ? "true" : "false"
        )
        if (listboxId && isOpen) {
          comboHost.setAttribute("aria-controls", listboxId)
        } else {
          comboHost.removeAttribute("aria-controls")
        }
        if (describedByIds.length) {
          comboHost.setAttribute("aria-describedby", describedByIds.join(" "))
        } else {
          comboHost.removeAttribute("aria-describedby")
        }
        if (invalidFeedbackMessage.value) {
          comboHost.setAttribute("aria-errormessage", invalidFeedbackId.value)
        } else {
          comboHost.removeAttribute("aria-errormessage")
        }
      }
      const clearBtn = root.querySelector(".vs__clear")
      if (clearBtn) {
        clearBtn.setAttribute(
          "aria-label",
          t("common.a11y_clear_field_selection", {
            field: resolvedComboboxAriaLabel.value
          })
        )
      }

      /*
       * When the dropdown is open, SRs often surface the options `role="listbox"` (e.g. “list with 25 items”)
       * without the field name. Mirror the combobox label onto the listbox so it matches the visible/external label.
       */
      if (listboxId && typeof document !== "undefined") {
        const listboxEl = document.getElementById(listboxId)
        if (listboxEl) {
          if (props.multiple) {
            listboxEl.setAttribute("aria-multiselectable", "true")
          } else {
            listboxEl.removeAttribute("aria-multiselectable")
          }
          const hasFloatingLabel = Boolean(resolvedVisibleLabel.value?.trim?.())
          const externalLabelId = getOrCreateExternalLabelId()
          const hasExternalLabel = Boolean(externalLabelId)
          const useExternalLabelledBy =
            props.hideFloatingLabel && Boolean(externalLabelledBy.value)
          if (hasExternalLabel) {
            listboxEl.setAttribute("aria-labelledby", externalLabelId)
            listboxEl.removeAttribute("aria-label")
          } else if (hasFloatingLabel) {
            listboxEl.setAttribute(
              "aria-labelledby",
              comboboxFloatingLabelId.value
            )
            listboxEl.removeAttribute("aria-label")
          } else if (useExternalLabelledBy) {
            listboxEl.setAttribute("aria-labelledby", externalLabelledBy.value)
            listboxEl.removeAttribute("aria-label")
          } else {
            listboxEl.removeAttribute("aria-labelledby")
            listboxEl.setAttribute(
              "aria-label",
              resolvedComboboxAriaLabel.value
            )
          }
          /* vue-select maps aria-selected to the type-ahead row only; use chosen option (APG listbox). */
          listboxEl
            .querySelectorAll('li[role="option"].vs__dropdown-option')
            .forEach((li) => {
              const chosen = li.classList.contains(
                "vs__dropdown-option--selected"
              )
              li.setAttribute("aria-selected", chosen ? "true" : "false")
            })
        }
      }

      syncVueSelectListboxAriaHidden()
    }

    watch(
      [
        resolvedComboboxAriaLabel,
        resolvedVisibleLabel,
        comboboxFloatingLabelId,
        () => props.clearable,
        () => props.hideFloatingLabel,
        () => props.labelledBy,
        () => props.searchable,
        () => props.filterable,
        optionsLengthSignal,
        modelValueSyncSignal,
        () => props.multiple,
        () => props.invalidFeedback,
        () => props.required
      ],
      () => syncVueSelectAria(),
      { flush: "post" }
    )

    watch(optionsLengthSignal, () => scheduleSyncNoMatchingOptionsLiveRegion())

    /* The list can open via pointer (@open) or keyboard (Arrow/Enter/Space set `open`
       directly through map-keydown). Watch the open state so an empty list announces
       "Sorry, no matching options" on open even without typing. */
    const dropdownOpenSignal = computed(() => !!vselectRef.value?.open)
    watch(dropdownOpenSignal, (isOpen, wasOpen) => {
      if (isOpen) scheduleEmptyStateChecksOnOpen()
      else clearOpenEmptyChecks()
      /* aria-expanded is set on the focused .vs__search input, but in this combobox the
         listbox is appended to body and focus context shifts on open, so the
         expanded/collapsed state isn't reliably auto-announced (Deque 2173040). Mirror it
         to the global live region. Skip the initial undefined->false settle. */
      if (isOpen === wasOpen) return
      const fieldName = resolvedComboboxAriaLabel.value?.trim?.() || ""
      const stateMsg = isOpen
        ? t("common.a11y_combobox_expanded")
        : t("common.a11y_combobox_collapsed")
      announce(fieldName ? `${fieldName}, ${stateMsg}` : stateMsg)
    })

    /** vue-select spinner uses in-place aria-live; NVDA often misses it — use global announcer (2171280). */
    const dropdownLoadingSignal = computed(
      () => !!vselectRef.value?.mutableLoading
    )
    watch(dropdownLoadingSignal, (loading, wasLoading) => {
      if (loading && !wasLoading) {
        announce(t("common.loading_more"))
      }
    })

    const isActivationPointerForThisField = (target) => {
      if (!target || typeof document === "undefined") return false
      const id = resolvedInputId.value
      const inputEl = document.getElementById(id)
      const root = vselectRef.value?.$el
      if (inputEl && (inputEl === target || inputEl.contains?.(target))) {
        return true
      }
      if (root?.contains?.(target)) return true
      try {
        const escaped =
          typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id
        const label = document.querySelector(`label[for="${escaped}"]`)
        if (label?.contains?.(target)) return true
      } catch {
        const label = document.querySelector(`label[for="${id}"]`)
        if (label?.contains?.(target)) return true
      }
      return false
    }

    const onDocumentPointerDownCapture = (e) => {
      pendingPointerFieldActivation.value = isActivationPointerForThisField(
        e.target
      )
    }

    /**
     * vue-select opens the list in onSearchFocus; that steals SR context before the field label
     * is announced. When openOnFocus is false, close again immediately unless the user used the
     * pointer on this control or its label (click to open). Synchronous close avoids emitting
     * `open` to parents (e.g. lazy-load fetch) before we collapse the menu.
     */
    const onVSelectSearchFocus = () => {
      scheduleEmptyStateAnnounceOnFocus()
      if (props.openOnFocus !== false) return
      const inst = vselectRef.value
      if (!inst) return
      if (!pendingPointerFieldActivation.value) {
        inst.open = false
      }
      pendingPointerFieldActivation.value = false
      syncVueSelectAria()
    }

    /*
     * Keeps a select-only combobox non-editable without a native `readonly` attribute
     * (Deque 2173745/2173751). `beforeinput` only fires when text would actually be
     * inserted/deleted, so arrow/enter/space navigation handled by vue-select is untouched.
     */
    const preventSelectOnlyTyping = (e) => {
      if (props.searchable !== false) return
      e.preventDefault()
    }

    /** When the list was closed, Arrow / Enter open; Space opens for non-filterable (readonly) selects. */
    const vueSelectMapKeydown = (handlers, selectVm) => {
      const openIfClosedThen = (originalHandler) => (e) => {
        if (!selectVm.open) {
          selectVm.open = true
          nextTick(() => {
            if (typeof originalHandler === "function") originalHandler(e)
          })
          return
        }
        if (typeof originalHandler === "function") return originalHandler(e)
      }
      if (handlers[38]) {
        handlers[38] = openIfClosedThen(handlers[38])
      }
      if (handlers[40]) {
        handlers[40] = openIfClosedThen(handlers[40])
      }
      const orig13 = handlers[13]
      handlers[13] = (e) => {
        if (!selectVm.open) {
          e.preventDefault()
          selectVm.open = true
          return
        }
        if (typeof orig13 === "function") return orig13(e)
      }
      if (selectVm.filterable === false) {
        const orig32 = handlers[32]
        handlers[32] = (e) => {
          if (!selectVm.open) {
            e.preventDefault()
            selectVm.open = true
            return
          }
          if (typeof orig32 === "function") return orig32(e)
        }
      }
      return handlers
    }

    /* Native focusin (bubbles) reliably fires for both searchable and non-searchable
       selects, via mouse or keyboard — unlike vue-select's @search:focus which only
       fires when a search input exists. Use it to announce the empty state on focus. */
    const onRootFocusIn = () => {
      scheduleEmptyStateAnnounceOnFocus()
    }

    /*
     * Announce the highlighted option as the user arrows through the open list
     * (Deque 2173040). vue-select sets aria-activedescendant on the input, but
     * because the listbox is appended to <body> NVDA does not reliably read the
     * active option. Mirror the highlighted option (with its position) into the
     * global live region — the same approach already used for the expanded/
     * collapsed state and the "no matching options" message.
     */
    const announceHighlightedOption = () => {
      const inst = vselectRef.value
      if (!inst || !inst.open || typeof document === "undefined") return
      const inputEl = document.getElementById(resolvedInputId.value)
      const listboxId = inputEl?.getAttribute?.("aria-controls") || ""
      const scope =
        (listboxId && document.getElementById(listboxId)) || document
      const highlighted = scope.querySelector(
        "li.vs__dropdown-option--highlight"
      )
      if (!highlighted) return
      const text = (highlighted.textContent || "").trim()
      if (!text) return
      const options = scope.querySelectorAll(
        'li[role="option"].vs__dropdown-option'
      )
      const index = Array.prototype.indexOf.call(options, highlighted)
      const total = options.length
      const isSelected = highlighted.classList.contains(
        "vs__dropdown-option--selected"
      )
      let message = text
      if (index >= 0 && total) {
        message = t(
          isSelected
            ? "common.a11y_combobox_active_option_selected"
            : "common.a11y_combobox_active_option",
          { label: text, index: index + 1, total }
        )
      } else if (isSelected) {
        message = `${text}, ${t("common.selected")}`
      }
      announce(message, { force: true })
    }

    const OPTION_NAV_KEYS = new Set([
      "ArrowDown",
      "ArrowUp",
      "Home",
      "End",
      "PageDown",
      "PageUp"
    ])
    const onRootKeydownAnnounceOption = (e) => {
      if (!OPTION_NAV_KEYS.has(e.key)) return
      /* Wait for vue-select to move its type-ahead pointer and re-render the
         highlighted option before reading it. */
      nextTick(() => {
        window.setTimeout(announceHighlightedOption, 30)
      })
    }

    onMounted(() => {
      document.addEventListener(
        "pointerdown",
        onDocumentPointerDownCapture,
        true
      )
      rootRef.value?.addEventListener("focusin", onRootFocusIn)
      rootRef.value?.addEventListener("keydown", onRootKeydownAnnounceOption)
      syncVueSelectAria()
      requestAnimationFrame(() => syncVueSelectAria())
    })

    onUnmounted(() => {
      if (noMatchingOptionsSyncTimer != null) {
        clearTimeout(noMatchingOptionsSyncTimer)
        noMatchingOptionsSyncTimer = null
      }
      clearOpenEmptyChecks()
      if (focusEmptyAnnounceTimer != null) {
        clearTimeout(focusEmptyAnnounceTimer)
        focusEmptyAnnounceTimer = null
      }
      document.removeEventListener(
        "pointerdown",
        onDocumentPointerDownCapture,
        true
      )
      rootRef.value?.removeEventListener("focusin", onRootFocusIn)
      rootRef.value?.removeEventListener("keydown", onRootKeydownAnnounceOption)
    })

    onUpdated(() => {
      syncVueSelectAria()
    })

    const onSearch = (value) => {
      if (
        props.openOnFocus === false &&
        props.filterable !== false &&
        vselectRef.value &&
        !vselectRef.value.open &&
        value != null &&
        String(value).length > 0
      ) {
        vselectRef.value.open = true
      }
      const slicedValue = clampValue(value, props.maxLength)
      emit("search", slicedValue)
      scheduleSyncNoMatchingOptionsLiveRegion()
    }
    const onChange = (value) => {
      if (props.readonly) return
      const slicedValue = clampValue(value, props.maxLength)
      emit("update:modelValue", slicedValue)
    }

    const onOpenSelect = (value) => {
      const slicedValue = clampValue(value, props.maxLength)
      emit("open", slicedValue)
      scheduleEmptyStateChecksOnOpen()
      nextTick(() => {
        syncVueSelectAria()
        requestAnimationFrame(() => syncVueSelectAria())
      })
    }

    const onCloseSelect = (value) => {
      const slicedValue = clampValue(value, props.maxLength)
      emit("close", slicedValue)
      clearOpenEmptyChecks()
      noMatchingOptionsMessage.value = ""
      nextTick(() => {
        syncVueSelectAria()
        requestAnimationFrame(() => syncVueSelectAria())
      })
    }

    const selectedOptions = computed(() => {
      return props.multiple &&
        props.modelValue &&
        Array.isArray(props.modelValue)
        ? props.modelValue.length
        : ""
    })

    const invalidFeedbackMessage = computed(() => {
      return props.invalidFeedback && Array.isArray(props.invalidFeedback)
        ? props.invalidFeedback[0]
        : props.invalidFeedback
    })
    const liveStatusMessage = computed(() => {
      if (!invalidFeedbackMessage.value) return ""
      const fieldName = resolvedComboboxAriaLabel.value || t("common.select")
      const requiredPrefix = isFieldRequired.value
        ? `${t?.("a11y.requiredFieldPrefix") || ""} `
        : ""
      return `${fieldName}. ${requiredPrefix}${invalidFeedbackMessage.value}`
    })

    watch(
      () => props.invalidFeedback,
      () => {
        if (invalidFeedbackMessage.value) {
          liveStatusRenderKey.value += 1
          /* Announce validation feedback reliably via the persistent global live region. */
          announce(liveStatusMessage.value, { assertive: true })
        }
      }
    )

    const valueToDescriptionPart = (option) => {
      if (option == null) return ""
      if (typeof option === "string" || typeof option === "number") {
        return String(option)
      }
      if (typeof option === "object") {
        const a =
          option.label ?? option.name ?? option.title ?? option.value ?? ""
        if (a == null || a === "") return ""
        if (typeof a === "object") {
          return (
            String(a.label ?? a.name ?? a.title ?? a.id ?? JSON.stringify(a)) ||
            ""
          )
        }
        return String(a)
      }
      return ""
    }

    const findOptionLabelForValue = (value) => {
      if (value == null || value === "") return ""
      if (typeof value === "object") {
        return valueToDescriptionPart(value)
      }

      const options = Array.isArray(props.options) ? props.options : []
      const reduceFn = typeof props.reduce === "function" ? props.reduce : null

      for (const opt of options) {
        if (opt == null) continue
        if (opt === value) return valueToDescriptionPart(opt)
        const reduced = reduceFn ? reduceFn(opt) : opt
        if (reduced === value || String(reduced) === String(value)) {
          return valueToDescriptionPart(opt)
        }
      }

      return ""
    }

    const selectedValueDescription = computed(() => {
      const normalized = Array.isArray(props.modelValue)
        ? props.modelValue
        : props.modelValue != null && props.modelValue !== ""
          ? [props.modelValue]
          : []

      if (!normalized.length) return ""

      const labels = normalized
        .map((opt) => valueToDescriptionPart(opt))
        .filter(Boolean)

      if (!labels.length) return ""

      return labels.join(", ")
    })

    const nativeTitleAttr = computed(() => {
      const t = props.title
      if (t != null && t !== "") {
        return typeof t === "string" || typeof t === "number"
          ? String(t)
          : undefined
      }

      const mv = props.modelValue
      if (mv == null || mv === "") return undefined

      if (Array.isArray(mv)) {
        if (!mv.length) return undefined
        const labels = mv.map(findOptionLabelForValue).filter(Boolean)
        return labels.length ? labels.join(", ") : undefined
      }

      const label = findOptionLabelForValue(mv)
      return label || undefined
    })

    const splitText = (text) => {
      const textArray = text.split(",")
      return textArray[0]
    }

    const vueSelectBindAttrs = computed(() => {
      // Do not pass `selected` or `title`: vue-select 4 does not declare them, so they
      // were falling through to the root div as HTML attributes (e.g. [object Object] on `selected="..."`).
      const attrs = {
        multiple: props.multiple,
        options: props.options,
        clearable: props.clearable,
        placeholder: props.placeholder,
        autoscroll: props.autoscroll,
        closeOnSelect: props.closeOnSelect,
        filterable: props.filterable,
        reduce: props.reduce,
        selectable: props.selectable,
        searchable: props.searchable,
        disabled: props.disabled,
        deselectFromDropdown: props.deselectFromDropdown,
        clearCacheOnBlur: props.clearCacheOnBlur,
        inputId: resolvedInputId.value,
        autocomplete: props.autocomplete
      }
      if (props.appendDropdownToBody) {
        attrs.appendToBody = true
        if (props.appendDropdownFixedPosition) {
          attrs.calculatePosition = appendBodyFixedCalculatePosition
        }
      }
      return attrs
    })

    /** Programmatically close vue-select menu (avoids reopening via search focus). */
    const closeDropdown = () => {
      const vs = vselectRef.value
      if (vs && typeof vs.closeSearchOptions === "function") {
        vs.closeSearchOptions()
      }
    }

    /** Scrollable listbox element (used by LazyLoadSelect for infinite scroll). */
    const getDropdownMenuElement = () => {
      const vs = vselectRef.value
      if (!vs) {
        return null
      }
      const refs = vs.$refs || vs.refs
      return refs?.dropdownMenu || null
    }

    return {
      vselectRef,
      rootRef,
      vueSelectBindAttrs,
      closeDropdown,
      getDropdownMenuElement,
      /** Re-apply combobox/listbox ARIA after async option loads (e.g. LazyLoadSelect). */
      syncComboboxAccessibility: syncVueSelectAria,
      /** Re-sync empty-filter live region after async option loads (e.g. LazyLoadSelect). */
      syncNoMatchingOptionsAnnouncement: syncNoMatchingOptionsLiveRegion,
      resolvedInputId,
      resolvedVisibleLabel,
      comboboxFloatingLabelId,
      resolvedComboboxAriaLabel,
      isFieldRequired,
      nativeTitleAttr,
      invalidFeedbackId,
      vueSelectMapKeydown,
      onVSelectSearchFocus,
      onSearch,
      onChange,
      onOpenSelect,
      onCloseSelect,
      selectedOptions,
      selectedValueDescription,
      selectedValueDescriptionId,
      liveStatusMessage,
      liveStatusMessageId,
      liveStatusRenderKey,
      noMatchingOptionsMessage,
      noMatchingOptionsMessageId,
      noMatchingOptionsLiveKey,
      invalidFeedbackMessage,
      splitText
    }
  }
}
</script>

<style></style>
